/**
 * NuevaCitaEquipoSlideOver.tsx — Escenario 2: Formulario de Nueva Cita Grupal
 *
 * Sigue el mismo patrón visual y de estado que NuevaCitaSlideOver.tsx.
 * Ref: docs/02_ESTANDARES_INGENIERIA/06_ESCENARIO_2_EQUIPOS_CLINICOS.md
 */

import React, { useState, useEffect } from 'react';
import { useSecretaryStore } from '../store/secretaryStore';
import { X, Search, Users, Clock, Calendar, Check, AlertCircle } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { crearCitaEquipo, checkConflictosEquipo } from '../services/TeamCalendarService';
import type { EquipoMiembro } from '../services/TeamCalendarService';

interface PerfilPaciente {
  perfil_usuario_id: string;
  nombres_completos: string;
  apellidos_completos: string;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export function NuevaCitaEquipoSlideOver() {
  const {
    isNuevaCitaEquipoOpen,
    setNuevaCitaEquipoOpen,
    selectedDate,
    misEquipos,
    addNotificacion,
    setLastActionText,
  } = useSecretaryStore();

  // ── Estado del formulario ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<PerfilPaciente[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<PerfilPaciente | null>(null);

  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [asistentesSeleccionados, setAsistentesSeleccionados] = useState<Set<string>>(new Set());

  const [fecha, setFecha] = useState(selectedDate || new Date().toISOString().split('T')[0]);
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [horaFin, setHoraFin] = useState('09:45');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync fecha con la del calendario cuando se abre
  useEffect(() => {
    if (isNuevaCitaEquipoOpen && selectedDate) {
      setFecha(selectedDate);
    }
  }, [isNuevaCitaEquipoOpen, selectedDate]);

  // Preseleccionar primer equipo disponible
  useEffect(() => {
    if (isNuevaCitaEquipoOpen && misEquipos.length > 0 && !selectedEquipoId) {
      setSelectedEquipoId(misEquipos[0].equipo_uuid);
    }
  }, [isNuevaCitaEquipoOpen, misEquipos, selectedEquipoId]);

  // Buscar pacientes por cedula / nombre
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 3) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsSearching(true);
    supabase
      .from('perfiles_usuario')
      .select('perfil_usuario_id, nombres_completos, apellidos_completos')
      .or(`nombres_completos.ilike.%${debouncedSearch}%,apellidos_completos.ilike.%${debouncedSearch}%`)
      .limit(6)
      .then(({ data }) => {
        if (!cancelled) {
          setSearchResults((data as PerfilPaciente[]) ?? []);
          setIsSearching(false);
        }
      });
    return () => { cancelled = true; };
  }, [debouncedSearch]);

  const handleHoraInicioChange = (val: string) => {
    setHoraInicio(val);
    try {
      const [h, m] = val.split(':').map(Number);
      const d = new Date();
      d.setHours(h, m + 45, 0, 0);
      setHoraFin(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    } catch { /* ignoring */ }
  };

  const toggleAsistente = (uuid: string) => {
    setAsistentesSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  };

  const handleClose = () => {
    setNuevaCitaEquipoOpen(false);
    setSelectedPaciente(null);
    setSearchQuery('');
    setSearchResults([]);
    setErrorMsg(null);
    setIsSaving(false);
  };

  const handleGuardar = async () => {
    if (!selectedPaciente) { setErrorMsg('Selecciona un paciente.'); return; }
    if (!selectedEquipoId) { setErrorMsg('Selecciona un equipo.'); return; }
    if (!fecha || !horaInicio || !horaFin) { setErrorMsg('Completa la fecha y hora.'); return; }

    const inicio = new Date(`${fecha}T${horaInicio}:00`).toISOString();
    const fin    = new Date(`${fecha}T${horaFin}:00`).toISOString();
    if (new Date(fin) <= new Date(inicio)) {
      setErrorMsg('La hora de fin debe ser posterior a la de inicio.');
      return;
    }

    const uuidsAsistentes = Array.from(asistentesSeleccionados);

    setIsSaving(true);
    setErrorMsg(null);

    // 1. Validar Conflictos Cruzados
    if (uuidsAsistentes.length > 0) {
      const { success: cSuccess, conflictos, message: cMessage } = await checkConflictosEquipo(uuidsAsistentes, inicio, fin);
      
      if (!cSuccess) {
        setErrorMsg(cMessage ?? 'Error al verificar disponibilidad de los médicos.');
        setIsSaving(false);
        return;
      }

      if (conflictos && conflictos.length > 0) {
        const hardBlock = conflictos.find(c => c.nivel === 'hard');
        if (hardBlock) {
          setErrorMsg(`Conflicto de Agenda: Un profesional seleccionado no está disponible. Motivo: ${hardBlock.motivo}`);
          setIsSaving(false);
          return;
        }
      }
    }

    // 2. Crear Cita
    const { success, message } = await crearCitaEquipo({
      equipo_uuid: selectedEquipoId,
      paciente_uuid: selectedPaciente.perfil_usuario_id,
      fecha_hora_inicio: inicio,
      fecha_hora_fin: fin,
      asistente_uuids: uuidsAsistentes,
    });

    if (!success) {
      setErrorMsg(message ?? 'Error al crear la cita.');
      setIsSaving(false);
      return;
    }

    addNotificacion('Cita grupal creada exitosamente.', 'success');
    setLastActionText(`Cita grupal creada para ${selectedPaciente.nombres_completos}`);
    handleClose();
  };

  if (!isNuevaCitaEquipoOpen) return null;

  const equipoActual = misEquipos.find(e => e.equipo_uuid === selectedEquipoId);
  const miembrosActuales: EquipoMiembro[] = (equipoActual?.miembros ?? []).filter(
    m => m.rol_equipo !== 'secretaria'
  );

  return (
    <>
      {/* Overlay Oscurecido */}
      <div 
        className="fixed inset-0 bg-slate-900/40 dark:bg-slate-900/60 backdrop-blur-sm z-100 animate-in fade-in duration-300"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Slide-over Panel Premium */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-110 bg-white dark:bg-slate-900 shadow-2xl z-101 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800">
        
        {/* Encabezado */}
        <div className="flex flex-col shrink-0">
          <div className="flex items-center justify-between p-5 pb-4" style={{ padding: '1.25rem', paddingBottom: '1rem', boxSizing: 'border-box' }}>
            <h2 className="text-xl font-black text-slate-800 dark:text-white flex items-center gap-3">
              <div className="h-8 w-8 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              Nueva Cita Grupal
            </h2>
            <button 
              type="button"
              onClick={handleClose}
              className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full text-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              aria-label="Cerrar ventana"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="h-px w-full bg-linear-to-r from-transparent via-slate-200 dark:via-slate-800 to-transparent" />
        </div>

        {/* Contenido Desplazable */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8 custom-scrollbar" style={{ padding: '1.5rem', boxSizing: 'border-box' }}>

          {/* Error */}
          {errorMsg && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-2xl animate-in fade-in">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-700 dark:text-red-400">{errorMsg}</p>
            </div>
          )}

          {/* SECCIÓN: Equipo y Paciente */}
          <section className="flex flex-col gap-4">
            
            {misEquipos.length > 1 && (
              <div className="mb-2">
                <label htmlFor="equipo-select" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block uppercase tracking-wider">
                  Equipo Clínico
                </label>
                <select
                  id="equipo-select"
                  value={selectedEquipoId}
                  onChange={e => { setSelectedEquipoId(e.target.value); setAsistentesSeleccionados(new Set()); }}
                  className="w-full h-14 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow cursor-pointer appearance-none"
                >
                  {misEquipos.map(eq => (
                    <option key={eq.equipo_uuid} value={eq.equipo_uuid}>{eq.nombre_display}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center gap-2 mb-1">
              <Search className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Datos del Paciente
              </h3>
            </div>
            
            <div className="relative">
              {selectedPaciente ? (
                <div className="flex items-center justify-between bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/50 p-4 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                      {selectedPaciente.nombres_completos.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-200 block">
                        {selectedPaciente.nombres_completos} {selectedPaciente.apellidos_completos}
                      </span>
                      <span className="text-xs text-slate-500 block">Paciente Seleccionado</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedPaciente(null); setSearchQuery(''); }}
                    className="p-2 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded-full text-indigo-600 dark:text-indigo-400 transition-colors cursor-pointer border-none bg-transparent"
                    title="Cambiar paciente"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      id="paciente-search"
                      type="text"
                      placeholder="Buscar por nombre o cédula..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      style={{ paddingLeft: '3rem', paddingRight: '1rem', boxSizing: 'border-box' }}
                      className="w-full h-14 pl-12 pr-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                  {(isSearching || searchResults.length > 0) && (
                    <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden z-20">
                      {isSearching && (
                        <div className="p-4 flex items-center justify-center gap-2 text-sm text-slate-500 font-medium">
                          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-indigo-500 animate-spin" />
                          Buscando paciente...
                        </div>
                      )}
                      {searchResults.length > 0 && (
                        <div className="flex flex-col py-1">
                          {searchResults.map((p) => (
                            <button
                              key={p.perfil_usuario_id}
                              type="button"
                              onClick={() => { setSelectedPaciente(p); setSearchQuery(''); setSearchResults([]); }}
                              className="w-full text-left px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-3 transition-colors cursor-pointer border-none bg-transparent"
                            >
                              <div className="h-10 w-10 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                                {p.nombres_completos.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 overflow-hidden">
                                <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate block">
                                  {p.nombres_completos} {p.apellidos_completos}
                                </span>
                                <span className="text-xs text-slate-500 truncate block">Paciente Registrado</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* SECCIÓN: Horario */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Reserva de Horario
              </h3>
            </div>
            
            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-3xl flex flex-col gap-5" style={{ padding: '1.25rem' }}>
              <div>
                <label htmlFor="fecha-input" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Fecha de la Cita</label>
                <div className="relative">
                  <input
                    id="fecha-input"
                    type="date"
                    value={fecha}
                    onChange={e => setFecha(e.target.value)}
                    style={{ paddingLeft: '1rem', paddingRight: '2.75rem', boxSizing: 'border-box' }}
                    className="w-full h-14 pl-4 pr-11 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="inicio-input" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Hora Inicio</label>
                  <div className="relative">
                    <input
                      id="inicio-input"
                      type="time"
                      value={horaInicio}
                      onChange={e => handleHoraInicioChange(e.target.value)}
                      className="w-full h-14 pl-2 pr-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label htmlFor="fin-input" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Hora Fin</label>
                  <div className="relative">
                    <input
                      id="fin-input"
                      type="time"
                      value={horaFin}
                      onChange={e => setHoraFin(e.target.value)}
                      className="w-full h-14 pl-2 pr-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECCIÓN: Asistentes clínicos */}
          {miembrosActuales.length > 0 && (
            <section className="flex flex-col gap-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                    Clínicos Asistentes (Opcional)
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (asistentesSeleccionados.size === miembrosActuales.length) {
                      setAsistentesSeleccionados(new Set());
                    } else {
                      setAsistentesSeleccionados(new Set(miembrosActuales.map(m => m.miembro_uuid)));
                    }
                  }}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer bg-transparent border-none"
                >
                  {asistentesSeleccionados.size === miembrosActuales.length ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </div>
              
              <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-3xl flex flex-col gap-3" style={{ padding: '1.25rem' }}>
                <p className="text-xs text-slate-500 mb-2">Selecciona qué miembros del equipo participarán activamente en esta cita.</p>
                {miembrosActuales.map(m => (
                  <button
                    key={m.miembro_uuid}
                    type="button"
                    onClick={() => toggleAsistente(m.miembro_uuid)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-sm transition-all cursor-pointer shadow-sm ${
                      asistentesSeleccionados.has(m.miembro_uuid)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-500 text-indigo-900 dark:text-indigo-100'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 transition-colors overflow-hidden ${
                      asistentesSeleccionados.has(m.miembro_uuid)
                        ? 'bg-indigo-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                    }`}>
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt={m.nombre_display} className="h-full w-full object-cover" />
                      ) : (
                        m.initials
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <span className="block font-bold text-base mb-0.5">{m.nombre_display}</span>
                      <span className="block text-xs font-medium text-slate-400 capitalize">{m.rol_equipo.replace('_', ' ')}</span>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      asistentesSeleccionados.has(m.miembro_uuid)
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-slate-300 dark:border-slate-600 bg-transparent'
                    }`}>
                      {asistentesSeleccionados.has(m.miembro_uuid) && (
                        <Check className="h-3.5 w-3.5 text-white" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0" style={{ padding: '1.5rem', paddingTop: '1rem', boxSizing: 'border-box' }}>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={isSaving}
            className={`w-full h-14 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg active:scale-[0.98] border-none outline-none ${
              isSaving 
                ? 'bg-slate-400 cursor-not-allowed shadow-none' 
                : 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20 hover:shadow-indigo-500/40 cursor-pointer'
            }`}
          >
            {isSaving ? (
              <div className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <Check className="h-5 w-5" />
            )}
            {isSaving ? 'Creando cita grupal...' : 'Agendar Cita Grupal'}
          </button>
        </div>
      </div>
    </>
  );
}
